import type { ApolloQueryResult } from '@apollo/client/core/core.cjs';
import { derived, get, readable, type Readable } from 'svelte/store';
import { query, type QueryReturn } from '$lib/apollo/SvelteApollo';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import {
    ThreadListPageDocument,
    type ThreadListPageQuery,
    type ThreadListPageQueryVariables,
} from '$gql/graphql';

export class ThreadListStore {
    private readonly apollo: ShareableContainer['apollo'];
    private readonly browser: ShareableContainer['browser'];
    private readonly mercureManager: ShareableContainer['mercureManager'];
    private readonly initialVariables: Omit<ThreadListPageQueryVariables, 'page'>;
    private readonly query: QueryReturn<
        ThreadListPageQuery,
        ThreadListPageQueryVariables
    >;

    constructor(params: {
        initialVariables: Omit<ThreadListPageQueryVariables, 'page'>;
        apollo: ShareableContainer['apollo'];
        browser: ShareableContainer['browser'];
        mercureManager: ShareableContainer['mercureManager'];
    }) {
        this.apollo = params.apollo;
        this.browser = params.browser;
        this.initialVariables = params.initialVariables;
        this.query = query(
            ThreadListPageDocument,
            {
                variables: { ...this.initialVariables, page: 1 },
                notifyOnNetworkStatusChange: true,
            },
            { apollo: this.apollo, browser: this.browser },
        );
        this.mercureManager = params.mercureManager;
        this.bindMercureEvents();
    }

    private bindMercureEvents(this: this) {
        const manager = this.mercureManager();

        manager.inSvelte().onPrivateEvent('inbox/thread-deleted', () => {
            this.refetch();
        });

        manager.inSvelte().onPrivateEvent('inbox/thread-mark-read', () => {
            this.refetch();
        });

        manager.inSvelte().onPrivateEvent('inbox/new-message', () => {
            this.refetch();
        });
    }

    changeVariables(
        this: this,
        variables: Omit<ThreadListPageQueryVariables, 'page'>,
    ): Promise<ApolloQueryResult<ThreadListPageQuery> | void> {
        return this.query.setVariables({ ...variables, page: 1 });
    }

    pagerInfo(
        this: this,
    ): Readable<ThreadListPageQuery['inboxThreads']['pagerInfo'] | null> {
        return derived(this.query.result, ($result, set) => {
            set($result.dataOrPrev?.inboxThreads.pagerInfo ?? null);
        });
    }

    refetch(this: this) {
        this.query.refetch().catch(console.error);
    }

    threads(
        this: this,
        page: number,
    ): Readable<{
        loading: boolean;
        results: ThreadListPageQuery['inboxThreads']['results'] | null;
        pagerInfo: ThreadListPageQuery['inboxThreads']['pagerInfo'] | null;
    }> {
        const initial = this.apollo().cache.readQuery({
            query: ThreadListPageDocument,
            returnPartialData: false,
        });

        return readable(
            {
                results: initial?.inboxThreads.results ?? null,
                pagerInfo: initial?.inboxThreads.pagerInfo ?? null,
                loading: initial == null,
            },
            (set) => {
                const { result, setVariables } = query(ThreadListPageDocument, {
                    variables: { ...get(this.query.variables), page },
                });

                const unsubs = [
                    result.subscribe(($result) => {
                        set({
                            results: $result.data?.inboxThreads.results ?? null,
                            pagerInfo: $result.data?.inboxThreads.pagerInfo ?? null,
                            loading: $result.loading,
                        });
                    }),

                    this.query.variables.subscribe(($variables) => {
                        setVariables({ ...$variables, page: page }).catch(console.error);
                    }),
                ];

                return () => {
                    unsubs.forEach((cb) => cb());
                };
            },
        );
    }

    loading(this: this): Readable<boolean> {
        return derived(this.query.result, ($result, set) => {
            set($result.loading);
        });
    }

    hasDataOrPrev(this: this): Readable<boolean> {
        return derived(this.query.result, ($result, set) => {
            set($result.dataOrPrev != null);
        });
    }
}
